import { ChevronDown, ChevronRight, GripVertical, Loader2, MessageSquarePlus, PencilLine, Play, Sparkles, Volume2, X, Trash2 } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  advanceStep,
  fetchBackgroundSounds,
  fetchProjectProfile,
  fetchScenarioAudio,
  fetchSelectedScenario,
  selectScenario,
  suggestSfxPositions,
  synthesizeScenarioAudio,
  type BackgroundSound,
} from "@/api/client";
import { useSessionStore } from "@/hooks/useSessionStore";
import { ParagraphBlock, NumberBadge } from "@/components/edition-text/ParagraphBlock";
import { ExpressionVocale, FloatingChip } from "@/components/edition-text/ExpressionVocale";
import { TaggedTextarea, parseTaggedText } from "@/components/edition-text/TaggedTextarea";
import { useParagraphDragAndDrop } from "@/hooks/useParagraphDragAndDrop";

type EditablePart = {
  titre: string;
  texte_narration: string;
  [key: string]: unknown;
};

type TaggedParagraph = { partie_id: number; titre: string; taggedText: string };



export function EditionTextView() {
  const navigate = useNavigate();
  const { sessionId, projectName, lastProjectName, setCurrentStep } = useSessionStore();
  const resolvedProjectName = projectName ?? lastProjectName;
  const [parts, setParts] = useState<EditablePart[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const {
    heldTag,
    setHeldTag,
    mousePos,
    draggedIdx,
    setDraggedIdx,
    dragOverIdx,
    setDragOverIdx,
    handleDrop,
  } = useParagraphDragAndDrop(setParts, setIsDirty);

  const selectionQuery = useQuery({
    queryKey: ["selected-scenario", sessionId],
    queryFn: () => fetchSelectedScenario(sessionId!),
    enabled: Boolean(sessionId),
  });

  const profileQuery = useQuery({
    queryKey: ["project-profile", resolvedProjectName],
    queryFn: () => fetchProjectProfile(resolvedProjectName!),
    enabled: Boolean(resolvedProjectName),
  });

  const audioQuery = useQuery({
    queryKey: ["scenario-audio", sessionId],
    queryFn: () => fetchScenarioAudio(sessionId!),
    enabled: Boolean(sessionId),
  });

  const isElevenLabs = profileQuery.data?.tts_provider === "elevenlabs";

  useEffect(() => {
    if (!selectionQuery.data) return;
    const raw = selectionQuery.data as Record<string, unknown>;
    const payload = (raw.scenario as Record<string, unknown> | undefined) ?? raw;
    const partiesRaw = Array.isArray(payload?.parties)
      ? (payload.parties as Array<Record<string, unknown>>)
      : [];
    setParts(
      partiesRaw.map((p) => ({
        ...p,
        titre: typeof p.titre === "string" ? p.titre : "",
        texte_narration: typeof p.texte_narration === "string" ? p.texte_narration : "",
      }))
    );
    setIsDirty(false);
  }, [selectionQuery.data]);

  const queryClient = useQueryClient();

  // Sync parts → window.__taggedParagraphs so ChatPanel can send them to the LLM
  // Also reads taggedOutput.parties[].taggedText as fallback (where Agent 3 stores data)
  useEffect(() => {
    const raw = selectionQuery.data as Record<string, unknown> | undefined;
    const taggedOutput = raw?.taggedOutput as Record<string, unknown> | undefined;
    const taggedParties = Array.isArray(taggedOutput?.parties) ? (taggedOutput!.parties as Array<Record<string, unknown>>) : [];

    const paragraphs: TaggedParagraph[] = parts.map((p, i) => {
      const tagged = taggedParties[i];
      const taggedText = (p.texte_narration && p.texte_narration.length > 0)
        ? p.texte_narration
        : ((tagged?.taggedText as string) ?? "");
      return { partie_id: i + 1, titre: p.titre, taggedText };
    });
    (window as Window & { __taggedParagraphs?: TaggedParagraph[] }).__taggedParagraphs = paragraphs;
    return () => {
      (window as Window & { __taggedParagraphs?: TaggedParagraph[] }).__taggedParagraphs = undefined;
    };
  }, [parts, selectionQuery.data]);

  // Listen for LLM edits via update_tagged_scenario tool
  useEffect(() => {
    const handler = async (e: Event) => {
      const { paragraphs } = (e as CustomEvent<{ paragraphs: TaggedParagraph[] }>).detail;
      if (!Array.isArray(paragraphs) || paragraphs.length === 0) return;
      // Update local parts state immediately (source of truth for the UI)
      setParts(
        paragraphs.map((p) => ({
          titre: p.titre,
          texte_narration: p.taggedText,
        }))
      );
      setIsDirty(true);
      // Persist to backend without triggering a refetch (would overwrite parts)
      if (!sessionId) return;
      const raw = (selectionQuery.data ?? {}) as Record<string, unknown>;
      const updatedParties = paragraphs.map((p) => ({ titre: p.titre, texte_narration: p.taggedText }));
      const merged: Record<string, unknown> = { ...raw };
      if (merged.scenario && typeof merged.scenario === "object") {
        merged.scenario = { ...(merged.scenario as Record<string, unknown>), parties: updatedParties };
      } else {
        merged.parties = updatedParties;
      }
      await selectScenario(sessionId, merged);
    };
    window.addEventListener("tagged-scenario-updated", handler);
    return () => window.removeEventListener("tagged-scenario-updated", handler);
  }, [sessionId, selectionQuery.data]);

  // Auto-save to backend 1.5s after last change so refresh doesn't lose edits
  useEffect(() => {
    if (!isDirty || !sessionId || !selectionQuery.data) return;
    const timer = setTimeout(async () => {
      const raw = selectionQuery.data as Record<string, unknown>;
      const activeParts = parts.filter((p) => !(p as EditablePart & { _deleted?: boolean })._deleted);
      const merged: Record<string, unknown> = { ...raw };
      if (merged.scenario && typeof merged.scenario === "object") {
        merged.scenario = { ...(merged.scenario as Record<string, unknown>), parties: activeParts };
      } else {
        merged.parties = activeParts;
      }
      await selectScenario(sessionId, merged);
    }, 1500);
    return () => clearTimeout(timer);
  }, [parts, isDirty, sessionId, selectionQuery.data]);

  const updatePart = (idx: number, patch: Partial<EditablePart>) => {
    setParts((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
    setIsDirty(true);
  };

  const deletePart = (idx: number) => {
    setParts((prev) => prev.map((p, i) => (i === idx ? { ...p, _deleted: true } : p)));
    setIsDirty(true);
  };

  const restorePart = (idx: number) => {
    setParts((prev) => prev.map((p, i) => {
      if (i !== idx) return p;
      const { _deleted: _, ...rest } = p as EditablePart & { _deleted?: boolean };
      return rest as EditablePart;
    }));
    setIsDirty(true);
  };

  // Drag and drop handled by hook

  const proceedToScenarioEdit = () => {
    setCurrentStep("scenario_edit");
    navigate("/step/scenario_edit");
  };

  const handleSwitchToQwen = async () => {
    if (!sessionId) return;
    setIsSwitching(true);
    try {
      const currentProfile = profileQuery.data;
      if (!currentProfile) {
        throw new Error("Profil du projet non disponible.");
      }
      await advanceStep(sessionId, "project_details", {
        notes: currentProfile.project_notes ?? "",
        audience: currentProfile.audience || undefined,
        tone: currentProfile.tone || undefined,
        target_duration: currentProfile.target_duration ?? 120,
        voice_instructions: currentProfile.voice_instructions || undefined,
        tts_provider: "qwen",
        include_citations: currentProfile.include_citations !== false,
        source_usage_level: currentProfile.source_usage_level || "modere"
      });
      await queryClient.invalidateQueries({ queryKey: ["project-profile", resolvedProjectName] });
      setGenerateError(null);
      // Wait for query invalidation to complete and retry audio synthesis
      setTimeout(() => {
        goToAudioEdition();
      }, 150);
    } catch (switchErr) {
      console.error("Failed to switch to Qwen:", switchErr);
      setGenerateError("Impossible de basculer vers Qwen local. Veuillez modifier le moteur dans les Détails du projet.");
    } finally {
      setIsSwitching(false);
    }
  };

  const goToAudioEdition = async () => {
    if (!sessionId) return;
    setGenerateError(null);
    setIsSaving(true);

    try {
      if (selectionQuery.data) {
        const raw = selectionQuery.data as Record<string, unknown>;
        const activeParts = parts.filter((p) => !(p as EditablePart & { _deleted?: boolean })._deleted);
        const merged: Record<string, unknown> = { ...raw };
        if (merged.scenario && typeof merged.scenario === "object") {
          merged.scenario = { ...(merged.scenario as Record<string, unknown>), parties: activeParts };
        } else {
          merged.parties = activeParts;
        }
        await selectScenario(sessionId, merged);
      }
      
      setCurrentStep("synthese_vocale");
      navigate("/step/synthese_vocale");
    } catch (err: any) {
      setGenerateError(err.message ?? "Erreur lors de la sauvegarde.");
    } finally {
      setIsSaving(false);
    }
  };

  const raw = selectionQuery.data as Record<string, unknown> | undefined;
  const payload = (raw?.scenario as Record<string, unknown> | undefined) ?? raw;
  const scenarioLabel =
    (raw?.scenario_index != null ? `Scénario ${raw.scenario_index}` : null) ?? "Scénario";
  const scenarioTitle =
    (payload?.titre as string | undefined) ??
    (payload?.title as string | undefined) ??
    "";
  const axeNarratif = (payload?.axe_narratif as string | undefined) ?? "";

  if (isGenerating) {
    return (
      <div className="mx-auto flex w-full max-w-[1100px] flex-col items-center justify-center gap-6 p-6">
        <div className="w-full rounded-[14px] border border-[#8EA4BD] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)] flex flex-col items-center gap-6 py-20 px-8">
          <Loader2 className="h-12 w-12 animate-spin text-[#007AFF]" />
          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="text-[20px] font-semibold text-[#0F172B]">Génération de l&apos;audio</h2>
            <p className="text-[14px] text-[#45556C]">
              Synthèse vocale en cours, veuillez patienter…
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4 p-6">
      {heldTag && (
        <div
          className="pointer-events-none fixed z-50 select-none"
          style={{ left: mousePos.x + 14, top: mousePos.y - 8 }}
        >
          <FloatingChip rawTag={heldTag} />
        </div>
      )}
      <div className="mx-auto w-full max-w-[1100px] rounded-[14px] border border-[#8EA4BD] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)]">

        <div className="flex w-full items-center gap-6 rounded-t-[14px] border-b-[0.8px] border-[#E2E8F0] bg-[#F8FAFC] px-5 py-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="inline-flex items-center gap-2">
              <PencilLine className="h-4 w-4 text-[#45556C]" />
              <h2 className="text-[14px] font-medium leading-[14px] text-[#45556C]">Édition du scénario</h2>
            </div>
            <p className="text-[14px] font-normal leading-5 text-[#45556C]">
              Modifier et baliser le scénario pour générer un audio.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-6 p-6">
          {selectionQuery.isLoading ? (
            <div className="flex items-center gap-2 text-[14px] text-[#45556C]">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : (
            <article className="flex w-full flex-col gap-6 rounded-[18px] border border-[#8EA4BD] bg-white p-[25px] shadow-[0_2px_10px_rgba(0,0,0,0.10)]">

              <div className="flex min-h-[60px] flex-col gap-2">
                <h3 className="text-[18px] font-semibold leading-[18px] text-[#1E293B]">{scenarioLabel}</h3>
                {scenarioTitle && (
                  <p className="text-[22px] font-bold leading-[24px] text-[#0F172B]">{scenarioTitle}</p>
                )}
                {axeNarratif && (
                  <span className="inline-flex min-h-8 h-auto w-fit items-center justify-center rounded-[40px] border border-[#E2E8F0] bg-white px-4 py-1.5 text-[14px] font-semibold leading-snug text-[#45556C]">
                    {axeNarratif}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 rounded-[14px] border-[0.8px] border-[#5BA9FF] bg-[#EFF6FF] p-[10px]">
                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(new Event("open-chat"));
                    // Small delay so the panel animates open before focusing input
                    setTimeout(() => window.dispatchEvent(new Event("focus-chat-input")), 220);
                  }}
                  className="inline-flex h-[38px] items-center gap-1 rounded-[12px] bg-[linear-gradient(135deg,#007AFF_0%,#8CC3FF_100%)] px-3 text-[14px] font-medium text-white"
                >
                  <Sparkles className="h-4 w-4" />
                  <span>Demander à l&apos;agent IA</span>
                </button>
                {isElevenLabs && <ExpressionVocale onPick={setHeldTag} />}
                {heldTag && (
                  <div
                    onClick={() => setHeldTag(null)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      setHeldTag(null);
                    }}
                    className="flex h-[38px] items-center gap-2 rounded-[12px] border border-dashed border-[#EF4444] bg-[#FEF2F2] px-3 py-1.5 text-[13px] font-medium text-[#EF4444] cursor-pointer hover:bg-[#FEE2E2] transition-colors"
                  >
                    <Trash2 className="h-4 w-4 shrink-0 text-[#EF4444]" />
                    <span>Cliquer ou déposer ici pour supprimer</span>
                  </div>
                )}
              </div>

              <div className="flex w-full flex-col gap-8 rounded-[12px] border-t border-[#E2E8F0] bg-white p-[18px]">
                {parts.length === 0 && (
                  <p className="text-[14px] text-[#94a3b8]">Aucun contenu disponible.</p>
                )}
                {parts.map((part, idx) => {
                  const isDeleted = !!(part as EditablePart & { _deleted?: boolean })._deleted;
                  return (
                    <div
                      key={idx}
                      className={`group flex w-full flex-col gap-2 rounded-lg transition-colors ${
                        dragOverIdx === idx && draggedIdx !== idx && !isDeleted
                          ? "border-t-2 border-[#007AFF]"
                          : "border-t-2 border-transparent"
                      } ${draggedIdx === idx ? "opacity-40" : ""}`}
                      onDragOver={(e) => { if (!isDeleted) { e.preventDefault(); setDragOverIdx(idx); } }}
                      onDrop={(e) => { e.preventDefault(); if (!isDeleted) handleDrop(idx); }}
                    >
                      <div
                        className={`inline-flex items-center gap-2 ${isDeleted ? "cursor-pointer rounded-md px-1 py-0.5 hover:bg-[#f1f5f9]" : ""}`}
                        onClick={isDeleted ? () => restorePart(idx) : undefined}
                        title={isDeleted ? "Cliquer pour restaurer cette partie" : undefined}
                      >
                        {!isDeleted && (
                          <div
                            draggable
                            onDragStart={() => setDraggedIdx(idx)}
                            onDragEnd={() => { setDraggedIdx(null); setDragOverIdx(null); }}
                            className="cursor-grab active:cursor-grabbing touch-none"
                            title="Déplacer cette partie"
                          >
                            <GripVertical className="h-4 w-4 text-[#CBD5E1] hover:text-[#94a3b8]" />
                          </div>
                        )}
                        <NumberBadge number={idx + 1} />
                        <input
                          type="text"
                          value={part.titre}
                          onChange={(e) => !isDeleted && updatePart(idx, { titre: e.target.value })}
                          readOnly={isDeleted}
                          placeholder="Titre de la partie"
                          className={`flex-1 rounded-md border border-transparent bg-transparent px-1 text-[14px] font-semibold outline-none transition-colors ${
                            isDeleted
                              ? "line-through text-[#94a3b8] cursor-pointer pointer-events-none"
                              : "text-[#0F172B] hover:border-[#E2E8F0] focus:border-[#007AFF]"
                          }`}
                        />
                        {isDeleted ? (
                          <span className="ml-1 text-[11px] font-normal text-[#94a3b8] opacity-0 group-hover:opacity-100 transition-opacity">
                            Restaurer
                          </span>
                        ) : parts.filter((p) => !(p as EditablePart & { _deleted?: boolean })._deleted).length > 1 && (
                          <button
                            type="button"
                            onClick={() => deletePart(idx)}
                            className="opacity-0 group-hover:opacity-100 inline-flex h-6 w-6 items-center justify-center rounded-full text-[#94a3b8] transition-all hover:bg-[#fee2e2] hover:text-[#ef4444]"
                            title="Supprimer cette partie"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      {!isDeleted && (
                        <TaggedTextarea
                          value={part.texte_narration}
                          onChange={(text) => updatePart(idx, { texte_narration: text })}
                          hideEffetSonore={!isElevenLabs}
                          placeholder="Texte de narration…"
                          heldTag={heldTag}
                          onTagPlaced={() => setHeldTag(null)}
                          onPickTag={setHeldTag}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </article>
          )}
        </div>
      </div>

      {generateError && (
        <div className="flex w-full flex-col gap-3 rounded-[12px] border border-[#FF3B30] bg-[#fff1f0] p-4 text-[13px] text-[#FF3B30]">
          <div className="flex items-start justify-between gap-3">
            <span className="font-medium leading-5">{generateError}</span>
          </div>
          <div className="flex items-center gap-3">
            {generateError.includes("ELEVENLABS_API_KEY") && (
              <button
                type="button"
                onClick={handleSwitchToQwen}
                disabled={isSwitching}
                className="rounded-[8px] bg-[#FF3B30] px-3 py-1.5 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {isSwitching ? "Configuration de Qwen..." : "Basculer sur Qwen local"}
              </button>
            )}
            <button
              type="button"
              onClick={proceedToScenarioEdit}
              className="text-[13px] font-semibold underline hover:no-underline"
            >
              Continuer sans audio
            </button>
          </div>
        </div>
      )}

      <div className="flex w-full items-center justify-end">
        <button
          type="button"
          onClick={goToAudioEdition}
          disabled={isSaving}
          className="inline-flex h-[38px] items-center gap-1 rounded-[12px] bg-[#007AFF] px-4 py-2 text-[14px] font-medium leading-[14px] text-white transition-colors hover:bg-[#006ae0] disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              <span>Suivant : Synthèse vocale</span>
              <ChevronRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export function EditionScenario() {
  const { sessionId, projectName } = useSessionStore();
  const queryClient = useQueryClient();

  const profileQuery = useQuery({
    queryKey: ["project-profile", projectName],
    queryFn: () => fetchProjectProfile(projectName!),
    enabled: Boolean(projectName),
  });

  const ttsProvider: "elevenlabs" | "qwen" =
    profileQuery.data?.tts_provider === "qwen" ? "qwen" : "elevenlabs";

  const selectionQuery = useQuery({
    queryKey: ["selected-scenario", sessionId],
    queryFn: () => fetchSelectedScenario(sessionId!),
    enabled: Boolean(sessionId),
  });

  const raw = selectionQuery.data as Record<string, unknown> | undefined;

  const taggedParagraphs: TaggedParagraph[] = (() => {
    if (!raw) return [];
    const taggedOutput = raw.taggedOutput as Record<string, unknown> | undefined;
    if (taggedOutput && Array.isArray(taggedOutput.parties) && taggedOutput.parties.length > 0) {
      return (taggedOutput.parties as Array<Record<string, unknown>>).map((p, i) => ({
        partie_id: (p.partie_id as number) ?? i + 1,
        titre: (p.titre as string) ?? `Partie ${i + 1}`,
        taggedText: (p.taggedText as string) ?? "",
      }));
    }
    const scenario = (raw.scenario as Record<string, unknown> | undefined) ?? raw;
    if (Array.isArray(scenario.parties)) {
      return (scenario.parties as Array<Record<string, unknown>>).map((p, i) => ({
        partie_id: i + 1,
        titre: (p.titre as string) ?? `Partie ${i + 1}`,
        taggedText: ((p.texte_narration ?? p.texte) as string) ?? "",
      }));
    }
    return [];
  })();

  const scenarioTitle = (() => {
    if (!raw) return "";
    const scenario = (raw.scenario as Record<string, unknown> | undefined) ?? raw;
    return (scenario.titre as string) ?? "";
  })();

  useEffect(() => {
    (window as Window & { __taggedParagraphs?: TaggedParagraph[] }).__taggedParagraphs = taggedParagraphs;
    return () => {
      (window as Window & { __taggedParagraphs?: TaggedParagraph[] }).__taggedParagraphs = undefined;
    };
  }, [taggedParagraphs]);

  useEffect(() => {
    (window as Window & { __ttsProvider?: string }).__ttsProvider = ttsProvider;
    return () => {
      (window as Window & { __ttsProvider?: string }).__ttsProvider = undefined;
    };
  }, [ttsProvider]);

  useEffect(() => {
    const handler = async (e: Event) => {
      const { paragraphs } = (e as CustomEvent<{ paragraphs: TaggedParagraph[] }>).detail;
      if (!sessionId || !raw) return;
      const updated: Record<string, unknown> = {
        ...raw,
        taggedOutput: {
          ...((raw.taggedOutput as Record<string, unknown>) ?? {}),
          parties: paragraphs,
        },
      };
      await selectScenario(sessionId, updated);
      queryClient.invalidateQueries({ queryKey: ["selected-scenario", sessionId] });
    };
    window.addEventListener("tagged-scenario-updated", handler);
    return () => window.removeEventListener("tagged-scenario-updated", handler);
  }, [sessionId, raw, queryClient]);

  if (!sessionId) {
    return <p className="p-6 text-sm text-muted-foreground">Demarrez une session.</p>;
  }

  if (selectionQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Chargement du scenario...
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] rounded-[14px] border border-[#8EA4BD] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
      <div className="flex w-full items-center gap-6 rounded-t-[14px] border-b-[0.8px] border-[#E2E8F0] bg-[#F8FAFC] px-5 py-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="inline-flex items-center gap-2">
            <PencilLine className="h-4 w-4 text-[#45556C]" />
            <h2 className="text-[14px] font-medium leading-[14px] text-[#45556C]">Edition du scenario</h2>
          </div>
          <p className="text-[14px] font-normal leading-5 text-[#45556C]">
            Modifier et baliser le scenario pour generer un audio.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-6 p-6">
        <article className="flex w-full flex-col gap-6 rounded-[18px] border border-[#8EA4BD] bg-white p-[25px] shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
          <div className="flex items-start justify-between gap-6">
            <div className="flex min-h-[109px] flex-1 flex-col gap-3">
              <h3 className="text-[18px] font-semibold leading-[18px] text-[#1E293B]">Scenario</h3>
              {scenarioTitle && (
                <p className="text-[22px] font-bold leading-[24px] text-[#0F172B]">{scenarioTitle}</p>
              )}
            </div>

            <div className="flex w-[245px] flex-col gap-[18px] rounded-[16px] border-t-[0.8px] border-[#E2E8F0] bg-[#F8FAFC] p-4">
              <div className="inline-flex items-center justify-between">
                <span className="text-[16px] font-semibold leading-[16px] text-[#0F172B]">Echantillon audio</span>
                <Volume2 className="h-4 w-4 text-[#45556C]" />
              </div>
              <div className="inline-flex items-center gap-3">
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-[#0F172B]"
                  aria-label="Lecture"
                >
                  <Play className="h-4 w-4 fill-[#0F172B]" />
                </button>
                <div className="flex-1">
                  <div className="h-2 w-full rounded-[30px] bg-[#E2E8F0]">
                    <div className="h-2 w-[42%] rounded-[30px] bg-[#0F172B]" />
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[12px] font-semibold leading-[12px] text-[#45556C]">
                    <span>0:00</span>
                    <span>4:05</span>
                  </div>
                </div>
                <Volume2 className="h-5 w-5 text-[#0F172B]" />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 rounded-[14px] border-[0.8px] border-[#5BA9FF] bg-[#EFF6FF] p-[10px]">
            <button
              type="button"
              className="inline-flex h-[38px] items-center gap-1 rounded-[12px] bg-[linear-gradient(135deg,#007AFF_0%,#8CC3FF_100%)] px-3 text-[14px] font-medium text-white"
            >
              <Sparkles className="h-4 w-4" />
              <span>Demander a l&apos;agent IA</span>
            </button>
            {ttsProvider === "elevenlabs" && <ExpressionVocale />}
            <button type="button" className="inline-flex items-center gap-1 text-[14px] font-medium text-[#0F172B]">
              <MessageSquarePlus className="h-4 w-4" />
              <span>Ajouter un commentaire</span>
            </button>
          </div>

          <div className="flex w-full flex-col gap-8 rounded-[12px] border-t border-[#E2E8F0] bg-white p-[18px]">
            {taggedParagraphs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun paragraphe a afficher.</p>
            ) : (
              taggedParagraphs.map((paragraph) => (
                <ParagraphBlock
                  key={paragraph.partie_id}
                  index={paragraph.partie_id}
                  title={paragraph.titre}
                  content={parseTaggedText(paragraph.taggedText, ttsProvider === "qwen")}
                />
              ))
            )}
          </div>
        </article>
      </div>
    </div>
  );
}
